import { create } from "zustand";
import { DIFFICULTIES, LEVEL_IDS } from "@/content/interview";
import { getRoles, getTypes } from "@/store/customContentStore";

const STORAGE_KEY = "hippo.interviewFilters";

const ALL_ROLES = () => getRoles().map((r) => r.id);
const ALL_TYPES = () => getTypes().map((t) => t.id);
const ALL_DIFFS = [...DIFFICULTIES];
const ALL_LEVELS = [...LEVEL_IDS];

interface Saved {
  roles: string[];
  types: string[];
  difficulties: string[];
  levels: string[];
}

function clean(ids: string[] | undefined, allowed: string[], fallback: string[]): string[] {
  const valid = (ids ?? []).filter((id) => allowed.includes(id));
  return valid.length ? valid : [...fallback];
}

function load(): Saved {
  const allRoles = ALL_ROLES();
  const allTypes = ALL_TYPES();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const s = JSON.parse(raw) as Partial<Saved>;
      return {
        roles: clean(s.roles, allRoles, allRoles),
        types: clean(s.types, allTypes, allTypes),
        difficulties: clean(s.difficulties, ALL_DIFFS, ALL_DIFFS),
        levels: clean(s.levels, ALL_LEVELS, ALL_LEVELS),
      };
    }
  } catch {
    /* ignore */
  }
  return { roles: [...allRoles], types: [...allTypes], difficulties: [...ALL_DIFFS], levels: [...ALL_LEVELS] };
}

function persist(roles: string[], types: string[], difficulties: string[], levels: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ roles, types, difficulties, levels }));
}

interface InterviewFilterState {
  roles: string[];
  types: string[];
  difficulties: string[];
  levels: string[];
  toggleRole: (id: string) => void;
  toggleType: (id: string) => void;
  toggleDifficulty: (id: string) => void;
  toggleLevel: (id: string) => void;
  setAllRoles: (on: boolean) => void;
  setAllTypes: (on: boolean) => void;
  setAllDifficulties: (on: boolean) => void;
  setAllLevels: (on: boolean) => void;
  reset: () => void;
}

function toggle(list: string[], id: string, order: string[]): string[] {
  const set = new Set(list);
  if (set.has(id)) set.delete(id);
  else set.add(id);
  return order.filter((x) => set.has(x));
}

export const useInterviewFilterStore = create<InterviewFilterState>((set, get) => ({
  ...load(),
  toggleRole: (id) => {
    const roles = toggle(get().roles, id, ALL_ROLES());
    persist(roles, get().types, get().difficulties, get().levels);
    set({ roles });
  },
  toggleType: (id) => {
    const types = toggle(get().types, id, ALL_TYPES());
    persist(get().roles, types, get().difficulties, get().levels);
    set({ types });
  },
  toggleDifficulty: (id) => {
    const difficulties = toggle(get().difficulties, id, ALL_DIFFS);
    persist(get().roles, get().types, difficulties, get().levels);
    set({ difficulties });
  },
  toggleLevel: (id) => {
    const levels = toggle(get().levels, id, ALL_LEVELS);
    persist(get().roles, get().types, get().difficulties, levels);
    set({ levels });
  },
  setAllRoles: (on) => {
    const roles = on ? ALL_ROLES() : [];
    persist(roles, get().types, get().difficulties, get().levels);
    set({ roles });
  },
  setAllTypes: (on) => {
    const types = on ? ALL_TYPES() : [];
    persist(get().roles, types, get().difficulties, get().levels);
    set({ types });
  },
  setAllDifficulties: (on) => {
    const difficulties = on ? [...ALL_DIFFS] : [];
    persist(get().roles, get().types, difficulties, get().levels);
    set({ difficulties });
  },
  setAllLevels: (on) => {
    const levels = on ? [...ALL_LEVELS] : [];
    persist(get().roles, get().types, get().difficulties, levels);
    set({ levels });
  },
  reset: () => {
    const roles = ALL_ROLES();
    const types = ALL_TYPES();
    persist(roles, types, [...ALL_DIFFS], [...ALL_LEVELS]);
    set({ roles, types, difficulties: [...ALL_DIFFS], levels: [...ALL_LEVELS] });
  },
}));
